import React, { useState } from 'react';
import { Send, Phone, Mail, MapPin, Facebook, Instagram } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CONTACT_HERO_BG = "https://customer-assets.emergentagent.com/job_c2e293f8-1738-4ed6-a581-e197b34b7fa1/artifacts/67152p73_ChatGPT%20Image%20Apr%209%2C%202026%2C%2009_12_11%20AM.png";

const Contact = () => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: 'Custom Bespoke Mandir Inquiry', message: '' });
  const [status, setStatus] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setStatus('');
    try {
      await axios.post(`${API}/contact`, form);
      setStatus('success');
      setForm({ name: '', email: '', phone: '', subject: 'Custom Bespoke Mandir Inquiry', message: '' });
    } catch {
      setStatus('error');
    }
    setSubmitting(false);
  };

  return (
    <div data-testid="contact-page">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden" data-testid="contact-hero-section">
        <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" style={{ backgroundImage: `url('${CONTACT_HERO_BG}')` }} />
        <div className="absolute inset-0 bg-black/40" />
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-20 md:py-28 text-center relative z-10">
          <p className="text-xs md:text-sm tracking-[0.3em] text-[#D4A843] uppercase mb-6" data-testid="contact-hero-subtitle">
            Contact Us
          </p>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-light text-white tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="contact-hero-title">
            Let's Connect
          </h1>
        </div>
      </section>

      {/* Contact Form & Info Section */}
      <section className="py-20 md:py-28 bg-white" data-testid="contact-form-section">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="space-y-8">
              <div className="bg-white/40 border border-[#D7C0A0] p-8" data-testid="contact-info">
                <h2 className="text-2xl font-medium mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Contact Info
                </h2>
                <div className="space-y-5">
                  <div className="flex items-start gap-4">
                    <Mail className="w-5 h-5 text-[#B87333] mt-0.5 shrink-0" />
                    <div>
                      <p className="font-medium text-sm">Email</p>
                      <p className="text-[#5D4037] text-sm">info@uutsu.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Phone className="w-5 h-5 text-[#B87333] mt-0.5 shrink-0" />
                    <div>
                      <p className="font-medium text-sm">Phone</p>
                      <p className="text-[#5D4037] text-sm">(214)-850-9000</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <MapPin className="w-5 h-5 text-[#B87333] mt-0.5 shrink-0" />
                    <div>
                      <p className="font-medium text-sm">Location</p>
                      <p className="text-[#5D4037] text-sm">Local Delivery & Pickup Available</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-[#2C1810] text-white p-8" data-testid="custom-order-info">
                <h3 className="text-xl font-medium mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Custom Orders
                </h3>
                <p className="text-sm text-white/80 leading-relaxed">
                  Every custom piece begins with a conversation. Tell us your vision — the wood type, dimensions, carvings, and finish you desire — and our master artisans will bring it to life.
                </p>
                <p className="text-sm text-[#B87333] mt-4 font-medium">
                  Typical turnaround: 4-8 weeks
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <form onSubmit={handleSubmit} className="bg-white/40 border border-[#D7C0A0] p-8 space-y-6" data-testid="contact-form">
                <h2 className="text-2xl font-medium mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Send Us a Message
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1 text-[#5D4037]">Name *</label>
                    <input
                      type="text"
                      required
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="w-full px-4 py-3 bg-white/60 border border-[#D7C0A0] focus:outline-none focus:border-[#B87333] transition-colors"
                      placeholder="Your name"
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1 text-[#5D4037]">Email *</label>
                    <input
                      type="email"
                      required
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="w-full px-4 py-3 bg-white/60 border border-[#D7C0A0] focus:outline-none focus:border-[#B87333] transition-colors"
                      placeholder="your@email.com"
                      data-testid="contact-email-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1 text-[#5D4037]">Phone</label>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className="w-full px-4 py-3 bg-white/60 border border-[#D7C0A0] focus:outline-none focus:border-[#B87333] transition-colors"
                      placeholder="+1 (555) 000-0000"
                      data-testid="contact-phone-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1 text-[#5D4037]">Subject</label>
                    <select
                      value={form.subject}
                      onChange={(e) => setForm({ ...form, subject: e.target.value })}
                      className="w-full px-4 py-3 bg-white/60 border border-[#D7C0A0] focus:outline-none focus:border-[#B87333] transition-colors"
                      data-testid="contact-subject-select"
                    >
                      <option>Custom Bespoke Mandir Inquiry</option>
                      <option>Custom Woodwork & Art</option>
                      <option>Artisan Metals Inquiry</option>
                      <option>Ayurvedic Aromas Inquiry</option>
                      <option>Bulk Order</option>
                      <option>General Inquiry</option>
                      <option>Other</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1 text-[#5D4037]">Message *</label>
                  <textarea
                    required
                    rows={5}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="w-full px-4 py-3 bg-white/60 border border-[#D7C0A0] focus:outline-none focus:border-[#B87333] transition-colors resize-none"
                    placeholder="Describe your custom project — dimensions, wood type, design preferences, timeline..."
                    data-testid="contact-message-input"
                  />
                </div>

                {status === 'success' && (
                  <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 text-sm" data-testid="contact-success-message">
                    Thank you! Your message has been sent. We'll get back to you within 24-48 hours.
                  </div>
                )}
                {status === 'error' && (
                  <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 text-sm" data-testid="contact-error-message">
                    Something went wrong. Please try again or email us directly.
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary w-full flex items-center justify-center gap-2"
                  data-testid="contact-submit-button"
                >
                  <Send className="w-4 h-4" />
                  {submitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Follow Us Section */}
      <section className="py-20 md:py-28" style={{ backgroundColor: '#F5F0E1' }} data-testid="contact-follow-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-medium text-[#1B2A4A] mb-10" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="contact-follow-title">
            Follow us @ Rasaa~TheEssence
          </h2>
          <div className="flex items-center justify-center gap-8">
            <a href="https://www.facebook.com/RASAATheEssence" target="_blank" rel="noopener noreferrer" className="text-[#1B2A4A] hover:text-[#D4A843] transition-colors" data-testid="contact-facebook-link">
              <Facebook className="w-10 h-10" />
            </a>
            <a href="https://www.instagram.com/rasaa.designs" target="_blank" rel="noopener noreferrer" className="text-[#1B2A4A] hover:text-[#D4A843] transition-colors" data-testid="contact-instagram-link">
              <Instagram className="w-10 h-10" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
